// @ts-nocheck
import React, { useEffect, useState } from 'react';
import {
  normalizeAppletId,
  persistAppletId,
  readCachedAppletId,
  type AppletId,
} from './appletRegistry';
import { loadAppletModule, type AppletModule } from './loadApplet';
import { fetchTenantForApplet } from './tenantApi';

/**
 * Runtime shell (same rule as arva branch):
 * 1. Start from cached tenant / appTemplate
 * 2. Call GET /api/auth/tenant/
 * 3. Load applet matching template_type (nsight | arva | saye-saman)
 */
export default function Shell() {
  const [appletId, setAppletId] = useState<AppletId>(() => readCachedAppletId());
  const [module, setModule] = useState<AppletModule | null>(null);
  const [ready, setReady] = useState(false);
  const [bootError, setBootError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      setReady(false);
      setBootError(null);

      try {
        const tenant = await fetchTenantForApplet();
        const nextId = tenant.appletId || readCachedAppletId();
        persistAppletId(nextId);
        if (!cancelled) setAppletId(nextId);

        const loaded = await loadAppletModule(nextId);
        loaded.applyActiveTemplate();
        if (tenant.templateType || tenant.companyLogo) {
          loaded.applyResolvedTemplate(tenant.templateType, tenant.companyLogo);
        }

        if (!cancelled) {
          setModule(loaded);
          setReady(true);
        }
      } catch (error) {
        console.error('Shell boot failed, falling back to cached applet', error);
        try {
          const fallbackId = normalizeAppletId(appletId);
          const loaded = await loadAppletModule(fallbackId);
          loaded.applyActiveTemplate();
          if (!cancelled) {
            setModule(loaded);
            setReady(true);
          }
        } catch (fallbackError) {
          if (!cancelled) {
            setBootError(fallbackError);
            setReady(true);
          }
        }
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
    // Boot once on mount; subsequent tenant changes are handled inside each applet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready || !module) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#ffffff',
        }}
      >
        <style>{`
          @keyframes shell-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
        <div
          role="status"
          aria-label="Loading"
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: '3px solid #e5e7eb',
            borderTopColor: '#2563eb',
            animation: 'shell-spin 0.75s linear infinite',
          }}
        />
      </div>
    );
  }

  if (bootError) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#ffffff',
          fontFamily: 'sans-serif',
          padding: 24,
          textAlign: 'center',
          color: '#334155',
        }}
      >
        Failed to load applet ({appletId}).
      </div>
    );
  }

  const AppletApp = module.App;
  return <AppletApp key={appletId} />;
}
